import React, { useState, FC } from 'react';
import classNames from 'classnames';

import WorkloadInfo from './WorkloadInfo.json';

import st from './Workload.module.css';

const getWorkloadTimesWidth = (isDay, workload, isDetailed) => {
  if (isDay) {
    const itemsCount = workload.length + 1;

    const styles = {
      width: `calc(100% / ${itemsCount - 1} * ${itemsCount})`,
    };

    return isDetailed
      ? {
          ...styles,
          maxWidth: `${(323 / (itemsCount - 1)) * itemsCount}px`,
        }
      : styles;
  }

  return {};
};

type WorkloadProps = {
  schedule: ScheduleTypeProps;
  isDetailed?: boolean;
  officeId?: number;
};

const Workload: FC<WorkloadProps> = ({
  isDetailed = false,
  officeId = 0,
  schedule,
}) => {
  const [workloadTypeIsDay, changeWorkloadType] = useState(true);

  const getBoundings = () => {
    const splittedSchedule = schedule?.currentDay?.split('–') || ['0', '0'];

    const firstHour =
      Number(splittedSchedule[0].replace(/[^\d:]/g, '').slice(0, 2)) || 8;
    const secondHour =
      Number(splittedSchedule[1].replace(/[^\d:]/g, '').slice(0, 2)) || 22;

    return { min: firstHour, max: secondHour };
  };

  const getWorkloadByOfficeId = () => {
    const workload = WorkloadInfo[officeId];

    const boundings = getBoundings();
    const averageWorkload = WorkloadInfo.average;
    const needsAverage = !workload;

    const startIndex = needsAverage
      ? averageWorkload.day.findIndex(({ time }) => time === boundings.min)
      : workload.day.findIndex(({ time }) => time === boundings.min);
    const lastIndex = needsAverage
      ? averageWorkload.day.findIndex(({ time }) => time === boundings.max)
      : workload.day.findIndex(({ time }) => time === boundings.max);

    const workloadDayPart = needsAverage
      ? averageWorkload.day.slice(startIndex, lastIndex)
      : workload.day.slice(
          startIndex === -1 ? 0 : startIndex,
          lastIndex === -1 ? workload.day.length : lastIndex
        );

    const startAverage =
      workloadDayPart[0].time === boundings.min
        ? []
        : averageWorkload.day.slice(
            averageWorkload.day.findIndex(({ time }) => time === boundings.min),
            averageWorkload.day.findIndex(
              ({ time }) => time === workloadDayPart[0].time
            )
          );

    const endAverage =
      workloadDayPart[workloadDayPart.length - 1].time === boundings.max
        ? []
        : averageWorkload.day.slice(
            averageWorkload.day.findIndex(
              ({ time }) => time === workloadDayPart[workloadDayPart.length - 1]
            ),
            averageWorkload.day.findIndex(
              ({ time }) => time === boundings.max
            ) + 1
          );

    return {
      day: [...startAverage, ...workloadDayPart, ...endAverage],
      week: needsAverage ? averageWorkload.week : workload.week,
    };
  };

  const workload = getWorkloadByOfficeId();

  const switchWorkloadType = () => changeWorkloadType(!workloadTypeIsDay);
  const getWorkloadSwitchItemClassName = (elementTypeIsDay) =>
    elementTypeIsDay === workloadTypeIsDay
      ? st.workloadSwitchItemActive
      : st.workloadSwitchItem;
  const getWorkloadTimesClassName = (elementTypeIsDay) =>
    elementTypeIsDay ? st.workloadTimes : st.workloadTimesWeek;
  const currentWorkload = workloadTypeIsDay ? workload.day : workload.week;

  const time = new Date();

  const currentHour = time.getHours();
  const currentDayOfWeek = time.getDay();

  const getColumnClassName = (item) => {
    if (item.time === currentHour || item.day === currentDayOfWeek) {
      return st.workloadColumnActive;
    }

    return st.workloadColumn;
  };

  const getHourClassName = (item) => {
    if (item.time === currentHour || item.time === currentDayOfWeek) {
      return st.workloadTimeActive;
    }

    return st.workloadTime;
  };

  const getLastHour = (item) => item[item.length - 1].time + 1;

  return (
    <div
      className={
        isDetailed ? st.workloadContainerDetailed : st.workloadContainer
      }
    >
      <h2 className={st.workloadTitle}>Загруженность</h2>
      <div className={st.workloadColumns}>
        {currentWorkload.map((item) => (
          <div
            key={item.time}
            className={getColumnClassName(item)}
            style={{ height: `${item.percentage}%` }}
          />
        ))}
      </div>

      <div
        className={getWorkloadTimesClassName(workloadTypeIsDay)}
        style={getWorkloadTimesWidth(
          workloadTypeIsDay,
          currentWorkload,
          isDetailed
        )}
      >
        {currentWorkload.map((item) => (
          <div
            key={`times-${item.time}`}
            className={classNames(getHourClassName(item), st.workloadTime)}
          >
            {item.time}
          </div>
        ))}
        {workloadTypeIsDay && (
          <div className={st.workloadTime}>{getLastHour(currentWorkload)}</div>
        )}
      </div>
      <div className={st.workloadSwitch}>
        <button
          type="button"
          className={getWorkloadSwitchItemClassName(true)}
          onClick={switchWorkloadType}
        >
          День
        </button>
        <span className={st.slash} />
        <button
          type="button"
          className={getWorkloadSwitchItemClassName(false)}
          onClick={switchWorkloadType}
        >
          Неделя
        </button>
      </div>
    </div>
  );
};

export default Workload;
